import React, { useContext, useRef, useEffect, useState, useCallback } from "react";
import { Card } from "react-bootstrap";
import { ThemeContext, PatientContext } from "../lib/main-context";
import theme from "../index.scss";
import "./SoapNotes.css";

import {
  getPatientSoapNotes,
  getSoapNote,
  postSoapNote,
} from "../api/adminRequests";
import { useTranslation } from "../i18n/TranslationProvider";

const SoapNotes = () => {
  const { t, language } = useTranslation();
  const { dark } = useContext(ThemeContext);
  const { patient } = useContext(PatientContext);
  const leftBoxRef = useRef(null);

  const [noteText, setNoteText] = useState("");
  const [pastNotes, setPastNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the list of past soap notes for this patient
  const fetchPastNotes = useCallback(async () => {
    if (!patient?.patientId) return;
    setLoadingNotes(true);
    setError(null);
    try {
      const response = await getPatientSoapNotes(patient.patientId);
      // Sort latest → oldest
      const sorted = [...(response.data || [])].sort(
        (a, b) => new Date(b.date_written) - new Date(a.date_written)
      );
      setPastNotes(sorted);
    } catch (err) {
      console.error("Failed to load past notes:", err);
      setError(t("dashboard.soapNotes.ui.loadPastError"));
    } finally {
      setLoadingNotes(false);
    }
  }, [patient?.patientId]);

  useEffect(() => {
    fetchPastNotes();
  }, [fetchPastNotes]);

  // ResizeObserver to keep left column width in sync
  useEffect(() => {
    const leftBox = leftBoxRef.current;
    if (!leftBox) return;
    const observer = new ResizeObserver(() => {
      const leftCol = leftBox.closest(".soap-notes-left");
      if (leftCol) {
        leftCol.style.width = `${leftBox.offsetWidth}px`;
        leftCol.style.flex = "none";
      }
    });
    observer.observe(leftBox);
    return () => observer.disconnect();
  }, []);

  // Click a past-note row → fetch full text via getSoapNote
  const handleNoteClick = async (note) => {
    if (!patient?.patientId) return;
    setLoadingNote(true);
    setSelectedNote(null);
    setError(null);
    try {
      const response = await getSoapNote(patient.patientId, note.id);
      setSelectedNote(response.data);
    } catch (err) {
      console.error("Failed to load note:", err);
      setError(t("dashboard.soapNotes.ui.loadNoteError"));
    } finally {
      setLoadingNote(false);
    }
  };

  // Save the note text via postSoapNote, then refresh the list
  const handleSave = async () => {
    if (!noteText.trim()) return;
    if (!patient?.patientId) return;
    setSaving(true);
    setError(null);
    try {
      await postSoapNote({ patientId: patient.patientId, text: noteText });
      setNoteText("");
      await fetchPastNotes();
    } catch (err) {
      console.error("Failed to save note:", err);
      setError(t("dashboard.soapNotes.ui.saveError"));
    } finally {
      setSaving(false);
    }
  };

  // Shared colours
  const boxStyle = {
    backgroundColor: dark ? "#1f1f1f" : "#f8f9fa",
    border: dark ? "1px solid #444" : "1px solid #dee2e6",
    color: dark ? "#aaa" : "#666",
  };

  const titleStyle = { color: dark ? theme.darkModeText : "#333" };

  return (
    <Card
      className="soap-notes-card"
      style={{
        backgroundColor: dark ? theme.darkModeSecondary : "#fff",
        color: dark ? theme.darkModeText : "#333",
        border: dark ? `2px solid ${theme.darkModeText}` : `2px solid ${theme.gray}`,
      }}
    >
      <Card.Body>
        <div className="soap-notes-container">

          {/* ── Left column: write a new note ── */}
          <div className="soap-notes-left">
            <h5 className="soap-notes-title" style={titleStyle}>
              {t("dashboard.soapNotes.notes")}
            </h5>

            <div
              ref={leftBoxRef}
              className="soap-notes-content soap-notes-resizable"
              style={{ ...boxStyle, padding: 0 }}
            >
              <textarea
                className="soap-notes-textarea"
                value={noteText}
                onChange={(e) => {
                  setNoteText(e.target.value);
                  if (selectedNote) setSelectedNote(null);
                }}
                placeholder={t("dashboard.soapNotes.writeANewNote")}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  padding: "10px",
                  color: dark ? "#ccc" : "#333",
                  fontSize: "0.9rem",
                  fontFamily: "inherit",
                }}
              />
            </div>

            {error && (
              <p style={{ color: "#e74c3c", fontSize: "0.8rem", marginTop: 4 }}>
                {error}
              </p>
            )}

            <button
              className="soap-notes-save-btn"
              onClick={handleSave}
              disabled={saving || !noteText.trim()}
            >
              {saving ? t("dashboard.soapNotes.ui.saving") : t("dashboard.soapNotes.save")}
            </button>
          </div>

          {/* ── Right column: past notes list + detail ── */}
          <div className="soap-notes-right">
            <h5 className="soap-notes-title" style={titleStyle}>
              {t("dashboard.soapNotes.pastNotes")}
            </h5>

            {/* Scrollable note list */}
            <div
              className="soap-notes-content soap-notes-past-list"
              style={{
                ...boxStyle,
                overflowY: "auto",
                maxHeight: "200px",
                padding: "6px 0",
              }}
            >
              {loadingNotes ? (
                <p className="soap-notes-placeholder">{t("common.loading")}</p>
              ) : pastNotes.length === 0 ? (
                <p className="soap-notes-placeholder">{t("dashboard.soapNotes.noPastNotesYet")}</p>
              ) : (
                pastNotes.map((note) => (
                  <div
                    key={note.id}
                    onClick={() => handleNoteClick(note)}
                    className="soap-notes-past-item"
                    style={{
                      padding: "6px 12px",
                      cursor: "pointer",
                      borderBottom: dark
                        ? "1px solid #333"
                        : "1px solid #e9ecef",
                      backgroundColor:
                        selectedNote?.id === note.id
                          ? dark
                            ? "#2a2a2a"
                            : "#e8f0fe"
                          : "transparent",
                      transition: "background-color 0.15s",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: dark ? "#bbb" : "#555",
                      }}
                    >
                      {new Date(note.date_written).toLocaleDateString(language, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                      {" — "}
                      {note.author}
                    </div>
                    <div
                      style={{
                        fontSize: "0.82rem",
                        color: dark ? "#888" : "#777",
                        marginTop: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {note.note}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Expanded view for selected note */}
            {(selectedNote || loadingNote) && (
              <>
                <h6
                  style={{
                    marginTop: 12,
                    marginBottom: 4,
                    color: dark ? theme.darkModeText : "#333",
                    fontSize: "0.85rem",
                  }}
                >
                  {loadingNote
                    ? t("dashboard.soapNotes.ui.loadingNote")
                    : `${selectedNote.author} · ${new Date(
                        selectedNote.date_written
                      ).toLocaleDateString(language, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}`}
                </h6>
                <div
                  className="soap-notes-content"
                  style={{
                    ...boxStyle,
                    padding: "10px",
                    minHeight: "80px",
                    fontSize: "0.88rem",
                    whiteSpace: "pre-wrap",
                    overflowY: "auto",
                    maxHeight: "160px",
                  }}
                >
                  {loadingNote ? "…" : selectedNote?.note}
                </div>
              </>
            )}
          </div>

        </div>
      </Card.Body>
    </Card>
  );
};

export default SoapNotes;